import MaterialTable from '@material-table/core';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { Tooltip, Box, IconButton } from '@mui/material';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import { confirmDialog } from 'common/ComfirmationDialog';
import * as Constant from 'common/Constant';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { store } from 'store/store';
import * as Actions from './actions/action';
import LogoImg from './components/logoImg';
import LogoUploaderDialog from './components/logoUploaderDialog';

const SearchIconWithRef = React.forwardRef((props, ref) => (
  <SearchIcon {...props} ref={ref} color="primary" />
));

const BrokerWhiteLabelManagement = () => {
  const tableRef = useRef();

  const [open, setOpen] = useState(false);
  const [targetData, setTargetData] = useState(false);
  const [pageSize, setPageSize] = useState(Constant.DEFAULT_SELECT_PAGE_SIZE);

  const shouldRefresh = useSelector((state) => state.whiteLabeling.shouldRefresh);

  const handleLogoUploaderOpen = (rowData) => {
    setTargetData({ ...rowData, TYPE: Constant.LOGO_BROKER_LOGO });
    setOpen(true);
  };

  const handleSave = (data) => {
    if (data && (data.lightLogoFile instanceof File || data.darkLogoFile instanceof File)) {
      store.dispatch(Actions.uploadBrokerLogo(data.code, data.lightLogoFile, data.darkLogoFile));
    }
  };

  const handleLogoUploaderClose = (data) => {
    handleSave(data);
    setOpen(false);
  };

  const deleteLogo = (brokerCode) => {
    confirmDialog(
      () => store.dispatch(Actions.deleteBrokerLogo(brokerCode)),
      'Are you sure you want to remove the broker logos (Tenants under this broker will fall back to default logos)?'
    );
  };

  const deleteLogoByType = (brokerCode, logoType) => {
    const logoNumber = logoType === 'light' ? 1 : 2;
    const otherLogoNumber = logoType === 'light' ? 2 : 1;
    confirmDialog(
      () => store.dispatch(Actions.deleteBrokerLogoByType(brokerCode, logoType)),
      `Are you sure you want to remove Logo ${logoNumber}? The system will use the default logo instead. Logo ${otherLogoNumber} will not be affected.`
    );
  };

  const refresh = () => {
    tableRef.current?.onQueryChange();
  };

  useEffect(() => {
    refresh();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (shouldRefresh !== Actions.SHOULD_REFRESH_INIT_VALUE) {
      refresh();
    }
  }, [shouldRefresh]);

  return (
    <>
      <MaterialTable
        title="White Label Management – Broker view"
        tableRef={tableRef}
        localization={{
          toolbar: { searchPlaceholder: 'Search for Name or Code' }
        }}
        columns={[
          { title: 'Name', field: 'name' },
          { title: 'Code', field: 'code' },
          {
            title: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box component="span">Logo 1</Box>
                <Tooltip title="Light background logo. Used in Tableau reports.">
                  <InfoOutlinedIcon
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { color: 'rgba(0, 0, 0, 0.54)' }
                    }}
                  />
                </Tooltip>
              </Box>
            ),
            field: 'code',
            render: (rowData) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <LogoImg data={rowData} url={AppConfig.FO_INITIAL_URL + '/open-api/logo/v2/broker/'} variant="light" />
                <Tooltip title="Remove Logo 1">
                  <IconButton
                    onClick={() => deleteLogoByType(rowData.code, 'light')}
                    sx={{ color: 'rgba(0, 0, 0, 0.87)', '&:hover': { color: 'rgba(0, 0, 0, 0.54)' } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          },
          {
            title: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box component="span">Logo 2</Box>
                <Tooltip title="Dark background logo. Used in the left navigation menu.">
                  <InfoOutlinedIcon
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { color: 'rgba(0, 0, 0, 0.54)' }
                    }}
                  />
                </Tooltip>
              </Box>
            ),
            field: 'code',
            cellStyle: {
              backgroundColor: Constant.LEFT_NAV_DARK_BLUE
            },
            render: (rowData) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <LogoImg data={rowData} url={AppConfig.FO_INITIAL_URL + '/open-api/logo/v2/broker/'} variant="dark" />
              </Box>
            )
          }
        ]}
        data={(query) =>
          new Promise((resolve) => {
            axios
              .post(AppConfig.FO_INITIAL_URL + `/broker/search`, {
                searchTerm: query.search,
                page: query.page,
                size: query.pageSize,
                direction: query.orderDirection
                  ? query.orderDirection.toUpperCase()
                  : Constant.DIRECTION_ASC,
                sortProperty: query.orderBy ? query.orderBy.field : 'name'
              })
              .then((m) => {
                let data = m.data;
                resolve({
                  data: data.content,
                  page: data.number,
                  size: data.size,
                  totalCount: data.totalElements
                });
              })
              .catch((err) => {
                // Surface the failure in dev tools instead of silently showing an empty table.
                // (If you prefer user-visible feedback, dispatch APPSTATE_SET_APP_ERROR here.)
                // eslint-disable-next-line no-console
                console.error('Failed to fetch brokers for white label management', err);
                resolve({
                  data: [],
                  page: 0,
                  totalCount: 0
                });
              });
          })
        }
        onChangeRowsPerPage={setPageSize}
        options={{
          selection: false,
          actionsColumnIndex: -1,
          search: true,
          exportButton: false,
          pageSizeOptions: Constant.DEFAULT_SEARCH_PAGE_SIZE,
          debounceInterval: 400,
          pageSize
        }}
        components={{ OverlayLoading: () => <div /> }}
        actions={[
          {
            icon: () => <RefreshIcon />,
            tooltip: 'Refresh Data',
            onClick: () => refresh(),
            isFreeAction: true
          },
          {
            position: 'row',
            icon: () => <EditIcon />,
            tooltip: 'Upload Logos',
            onClick: (_, rowData) => handleLogoUploaderOpen(rowData)
          },
          {
            icon: () => <DeleteIcon />,
            tooltip: 'Remove Logo 2',
            onClick: (_, rowData) => deleteLogoByType(rowData.code, 'dark')
          }
        ]}
        icons={{
          Search: SearchIconWithRef
        }}
      />
      <LogoUploaderDialog
        open={open}
        onClose={handleLogoUploaderClose}
        target={targetData}
      />
    </>
  );
};

export default BrokerWhiteLabelManagement;
